import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'

import { anchorClass } from '@/common/const'
import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { t } from '@/localization/localization'

import styles from './toc.scss?inline'

type Heading = { level: number; id: any; text: any }

/**
 * `dap-ds-toc`
 * @summary A table of contents (TOC) is a list of links that allows users to quickly navigate to sections within a document.
 * @element dap-ds-toc
 * @title - toc
 *
 * @property { 'xs' | 'sm' | 'md' | 'lg' } size - The size of the TOC. Default is `lg.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg").
 *
 * @event {{ selectedItem: HTMLAnchorElement, anchorElement: IntersectionObserverEntry | HTMLElement }} dds-anchor-change - Event that is triggered when the anchor changes.
 *
 * @csspart base - The main container of the toc.
 *
 * @cssproperty --dds-toc-border-width - Width of the TOC border. (default: var(--dds-border-width-base))
 * @cssproperty --dds-toc-border-color - Color of the TOC border. (default: var(--dds-border-neutral-divider))
 * @cssproperty --dds-toc-title-color - Color of the TOC title. (default: var(--dds-text-neutral-strong))
 * @cssproperty --dds-toc-title-font-size - Font size of the TOC title. (default: var(--dds-font-base))
 * @cssproperty --dds-toc-title-font-weight - Font weight of the TOC title. (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-toc-title-line-height - Line height of the TOC title. (default: 1.25)
 * @cssproperty --dds-toc-link-color - Color of the TOC links. (default: var(--dds-button-subtle-text-neutral-enabled))
 * @cssproperty --dds-toc-link-active-color - Color of the active TOC link. (default: var(--dds-button-subtle-text-enabled))
 * @cssproperty --dds-toc-link-border-color - Border color of the active TOC link. (default: var(--dds-border-brand-base))
 * @cssproperty --dds-toc-link-line-height - Line height of the TOC links. (default: 1.2)
 * @cssproperty --dds-toc-link-padding - Padding of the TOC links. (default: var(--dds-spacing-200))
 * @cssproperty --dds-toc-indent-level-0 - Indent level of the TOC items. (default: var(--dds-spacing-400))
 * @cssproperty --dds-toc-indent-level-1 - Indent level of the TOC items. (default: var(--dds-spacing-800))
 * @cssproperty --dds-toc-indent-level-2 - Indent level of the TOC items. (default: var(--dds-spacing-1200))
 * @cssproperty --dds-toc-indent-level-3 - Indent level of the TOC items. (default: var(--dds-spacing-1600))
 * @cssproperty --dds-toc-indent-level-4 - Indent level of the TOC items. (default: var(--dds-spacing-2000))
 * @cssproperty --dds-toc-indent-level-5 - Indent level of the TOC items. (default: var(--dds-spacing-2400))
 */
export default class DapDSTOC extends SizedMixin(DdsElement) {
  static tagName = 'dap-ds-toc'

  /** The root element to observe [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#root) */
  @property() root: string = ''

  /** The margin around the root element to observe [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#rootmargin) */
  @property() rootMargin: string = '0px 0px -80% 0px'

  /** The margin around the last anchor to observe [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#rootmargin) */
  @property() lastAnchorRootMargin: string = '0px 0px -50% 0px'
  /** The threshold to trigger [`IntersectionObserver`](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#threshold) */
  @property({ type: Array }) threshold: number[] = [0, 0.1, 0.25, 0.5, 0.75, 1]

  /** The minimum heading level to include in the TOC. The default value is `2`. */
  @property({ type: Number }) minHeadingLevel: number = 2

  /** The maximum heading level to include in the TOC. The default value is `3`. */
  @property({ type: Number }) maxHeadingLevel: number = 3

  /** Whether to indent the TOC items. */
  @property({ type: Boolean }) noIndent: boolean = false

  /** The header of the TOC. */
  @property() header: string

  private _observer: IntersectionObserver
  private _lastAnchorObserver: IntersectionObserver
  private _anchors: HTMLElement[]
  private _activeAnchor: HTMLAnchorElement
  private _selectors: string[] = []
  private _items: Heading[]
  private _intersectionData: Map<
    HTMLElement,
    {
      isIntersecting: boolean
      intersectionRatio: number
      boundingRect: DOMRect
    }
  > = new Map()
  private _debounceTimer: number | null = null

  static readonly styles = unsafeCSS(styles)

  constructor() {
    super()
    // Use multiple thresholds for more precise intersection detection
    this.threshold = [0.1]
    this.size = 'lg'
  }

  get activeAnchor() {
    return this._activeAnchor
  }

  get anchors() {
    return this._anchors
  }

  get items() {
    return this._items
  }

  connectedCallback() {
    super.connectedCallback()

    const options = {
      root: this.root ? document.querySelector(this.root) : null,
      rootMargin: this.rootMargin,
      threshold: this.threshold,
    }

    this._observer = new IntersectionObserver(this._handleIntersect, options)

    this._lastAnchorObserver = new IntersectionObserver(this._handleIntersect, {
      ...options,
      rootMargin: this.lastAnchorRootMargin,
    })

    window.addEventListener('scroll', this.onScroll)
  }

  protected async firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    super.firstUpdated(_changedProperties)
    await this.renderTree()
  }

  async attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    super.attributeChangedCallback(name, oldValue, newValue)
    await this.renderTree()
  }

  async updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    await this.renderTree()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this._observer) {
      this._observer?.disconnect()
      this._lastAnchorObserver?.disconnect()
      window.removeEventListener('scroll', this.onScroll)
    }
  }

  private readonly onScroll = () => {
    if (
      this._anchors &&
      this._anchors.length > 0 &&
      Math.round(window.scrollY + window.innerHeight) ===
        document.body.scrollHeight
    ) {
      const entry = this._anchors[this._anchors.length - 1]
      const link = this.shadowRoot!.querySelector(`[href="#${entry.id}"`)
      if (link) {
        this.setActiveAnchor(link as HTMLAnchorElement, link as HTMLElement)
      }
    }
  }

  async renderTree() {
    this._anchors = this.getAnchors(
      !this.minHeadingLevel ? 2 : this.minHeadingLevel,
      !this.maxHeadingLevel ? 3 : this.maxHeadingLevel,
    )

    if (this._anchors.length === 0 || !this._anchors) {
      return
    }

    this._anchors.forEach((anchor, index) => {
      if (index === this._anchors.length - 1)
        this._lastAnchorObserver?.observe(anchor)
      else {
        this._observer?.observe(anchor)
      }
    })

    this._items = await this.getHeaders()
    const tree = this.buildTree(this._items)

    const baseElement = document.createElement('div')
    baseElement.setAttribute('part', 'base')
    baseElement.classList.add('toc')

    const base = `
        <span class="toc-title toc-title--${this.effectiveSize}">${this.header ?? t('tocTitle')}</span>
        <nav aria-label='${this.header ?? t('tocTitle')}''>${tree}</nav>`

    baseElement.innerHTML = base

    if (!this.shadowRoot) return

    this.shadowRoot.removeChild(this.shadowRoot.childNodes[0])
    this.shadowRoot.appendChild(baseElement)
  }

  getAnchors(minHeadingLevel: number, maxHeadingLevel: number): HTMLElement[] {
    this._selectors = []
    for (let i = minHeadingLevel; i <= maxHeadingLevel; i += 1) {
      this._selectors.push(`h${i}.${anchorClass}`)
      this._selectors.push(`dap-ds-typography[variant=h${i}][anchor]`)
    }

    return Array.from(document.querySelectorAll(this._selectors.join()))
  }

  setActiveAnchor(
    element: HTMLAnchorElement,
    entry: IntersectionObserverEntry | HTMLElement,
  ) {
    this._activeAnchor?.removeAttribute('aria-current')
    element.setAttribute('aria-current', 'true')
    this._activeAnchor = element

    this.emit('dds-anchor-change', {
      selectedItem: element,
      anchorElement: entry,
    })
  }

  private readonly getHeaders = async () => {
    const headers: Heading[] = []

    await this._anchors.forEach(async (el, index) => {
      await (el as any).updateComplete

      const textContent =
        el.getAttribute('data-text') ||
        el.innerText?.trim() ||
        el.textContent?.trim() ||
        ''
      const id =
        el.id ||
        el.innerText?.toLowerCase().split(' ').join('-') ||
        textContent.toLowerCase().split(' ').join('-')

      if (!el.id && id) {
        let count = 0

        if (this._anchors.some(anchor => anchor.id === id)) {
          count++
        }

        while (this._anchors.some(anchor => anchor.id === `${id}-${count}`)) {
          count++
        }

        if (count === 0) {
          el.setAttribute('id', id)
        } else {
          el.setAttribute('id', `${id}-${count}`)
        }
      }

      let level = this._selectors.indexOf(
        `${el.tagName.toLowerCase()}.${anchorClass}`,
      )

      if (level === -1) {
        level = this._selectors.indexOf(
          `dap-ds-typography[variant=${el.getAttribute('variant')}][anchor]`,
        )
      }

      headers[index] = {
        id,
        text: textContent,
        level: Math.floor(level / 2),
      }
    })

    return headers
  }

  buildTree(headers: any[]) {
    const list: string[] = []
    let nextLevelHeaders: any[] = []
    let lastLevel = -1

    if (headers.length === 0) {
      return ''
    }

    const buildSubTree = () => {
      if (nextLevelHeaders.length > 0) {
        list[list.length - 1] += this.buildTree(nextLevelHeaders)
      }
    }

    headers.forEach((h: Heading) => {
      if (lastLevel !== -1 && lastLevel < h.level) {
        nextLevelHeaders.push(h)
        return
      }

      buildSubTree()

      lastLevel = h.level
      list.push(
        `<a href="#${h.id}" class="toc-link toc-link--${this.effectiveSize}">${h.text}<span class="toc-link__high-contrast"></span></a>`,
      )
      nextLevelHeaders = []
    })

    buildSubTree()

    const listHTML = list
      .map(
        i => `<li class="toc-item ${'toc-item-level-' + lastLevel}">${i}</li>`,
      )
      .join('')

    return `<ul class="${clsx({
      'toc--no-indent': this.noIndent,
    })}">${listHTML}</ul>`
  }

  private readonly _handleIntersect = (
    entries: IntersectionObserverEntry[],
  ) => {
    // Update intersection data for all entries
    entries.forEach(entry => {
      this._intersectionData.set(entry.target as HTMLElement, {
        isIntersecting: entry.isIntersecting,
        intersectionRatio: entry.intersectionRatio,
        boundingRect: entry.boundingClientRect,
      })
    })

    // Debounce the active anchor selection to prevent rapid changes
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
    }

    this._debounceTimer = window.setTimeout(() => {
      this._updateActiveAnchor()
    }, 50) // 50ms debounce
  }

  private readonly _updateActiveAnchor = () => {
    const viewportCenter = window.innerHeight / 2
    let bestAnchor: HTMLElement | null = null
    let bestScore = -Infinity

    // Find the best anchor based on intersection ratio and distance from viewport center
    this._intersectionData.forEach((data, anchor) => {
      if (!data.isIntersecting) return

      const distanceFromCenter = Math.abs(
        data.boundingRect.top + data.boundingRect.height / 2 - viewportCenter,
      )
      const intersectionScore = data.intersectionRatio

      // Calculate a score that prioritizes elements closer to viewport center
      // and with higher intersection ratios
      const score =
        intersectionScore - (distanceFromCenter / window.innerHeight) * 0.5

      if (score > bestScore) {
        bestScore = score
        bestAnchor = anchor as HTMLElement
      }
    })

    if (bestAnchor && (bestAnchor as HTMLElement).id) {
      const link = this.shadowRoot!.querySelector(
        `[href="#${(bestAnchor as HTMLElement).id}"`,
      )
      if (link && link !== this._activeAnchor) {
        this.setActiveAnchor(
          link as HTMLAnchorElement,
          bestAnchor as HTMLElement,
        )
      }
    }
  }
}
